import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { getPriceList } from './frequency-selector.graphql'
import { getCustomValue, useCore } from '@danone-global/internal/react/core'
import { Grid } from '@danone-global/internal/react/components/ui'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { FormattedMessage } from 'react-intl'
import DOMPurify from 'dompurify';

export interface Props {
  sku: string | number
  channel?: string
}

interface PriceChannel {
  id: string
  text: string
  priceIncentiveText: string
  localizedpriceIncentiveText: string
}

export const useStyles = makeStyles(
  () => ({
    root: {
      marginLeft: '20px',
    },

  }),
  { name: 'FrequencySelector' },
)

export const WidgetFrequencySelector: React.FC<Props> = ({
  sku: providedSku,
  channel: defaultChannelActive = '',
}) => {
  const Fieldset: FunctionComponent<any> = (props: JSX.IntrinsicAttributes & React.ClassAttributes<HTMLFieldSetElement> & React.FieldsetHTMLAttributes<HTMLFieldSetElement>) => <fieldset {...props} />;
  
  const classes = useStyles()
  const core = useCore()
  const [channelIdActive, setChannelIdActive] = useState(defaultChannelActive)
  const sku = React.useMemo(() => `${providedSku}`, [providedSku])
  const [data, setData] = useState<any>(null)
  const { features } = useCore()
  const useLocalizedPriceIncentiveText=Boolean(features.useLocalizedPriceIncentiveText);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(`dtc-send-price-channel-${sku}`, {
        detail: { channelId: channelIdActive },
      }),
    )
  }, [channelIdActive, sku])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async() => {
    try {
      const data = await getPriceList(core, true, sku)
      setData(data)
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const channels: PriceChannel[] =
    data?.variant?.prices?.map((price: { channel: { id: any; name: any }; custom: { customFieldsRaw: any[] } }) => ({
      id: price?.channel?.id || '',
      text: price?.channel?.name || '',
      priceIncentiveText: DOMPurify.sanitize(price?.custom?.customFieldsRaw?.find(
        (item) => item.name === 'priceIncentiveText',
      )?.value),
      localizedpriceIncentiveText: DOMPurify.sanitize(price?.custom?.customFieldsRaw?.find(
        (item) => item.name === 'localizedpriceIncentiveText',
      )?.value[core.localeConfig.locale])
    })) || []

  const availableForSubscriptionData = data?.masterVariant?.attributesRaw
  const isAvailableForSubscription = availableForSubscriptionData && getCustomValue(availableForSubscriptionData, "availableForSubscription")

  if (!isAvailableForSubscription) {
    return null
  }

  return (
    <Grid
      className={'dtc-widget-frequency-selector'}
      direction="column"
      spacing={8}
      container
      component={Fieldset}
    >
      <Grid item className={'dtc-widget-frequency-selector-input'} data-testid={`widget-frequency-selector`}>
        <label htmlFor={`subscription-${sku}`}>
          <input
            type="radio"
            id={`subscription-${sku}`}
            name={`subscription-${sku}`}
            data-testid={`subscription-${sku}`}
            checked={!channelIdActive}
            onChange={() => {
              setChannelIdActive('')
            }}
            />       
          <FormattedMessage
            defaultMessage="One time purchase"
            id="widgets.frequencySelector.oneTimePurchase"
            tagName="span"
            />
        </label>
      </Grid>
      {
       !useLocalizedPriceIncentiveText? (channels?.filter(item => item.text !== '')?.map(({ id, text, priceIncentiveText }) => (
          <React.Fragment key={id}>
            <Grid item className={'dtc-widget-frequency-selector-input'} data-testid={`widget-frequency-selector-${id}`}>
              <label htmlFor={`subscription-${sku}-${id}`}>
                <input
                  type="radio"
                  id={`subscription-${sku}-${id}`}
                  name={`subscription-${sku}-${id}`}
                  data-testid={`subscription-${sku}-${id}`}
                  checked={channelIdActive === id}
                  onChange={() => {
                    setChannelIdActive(id)
                  }}
                />
                <span>{text}</span>
              </label>
              <label className={clsx('dtc-widget-frequency-selector__priceincentiveText', classes.root)} htmlFor={`subscription-${sku}-${id}`} dangerouslySetInnerHTML={ { __html: priceIncentiveText }}></label>
            </Grid>
          </React.Fragment>
        ))):(channels?.filter(item => item.text !== '')?.map(({ id, text, localizedpriceIncentiveText }) => (
          <React.Fragment key={id}>
            <Grid item className={'dtc-widget-frequency-selector-input'} data-testid={`widget-frequency-selector-${id}`}>
              <label htmlFor={`subscription-${sku}-${id}`}>
                <input
                  type="radio"
                  id={`subscription-${sku}-${id}`}
                  name={`subscription-${sku}-${id}`}
                  data-testid={`subscription-${sku}-${id}`}
                  checked={channelIdActive === id}
                  onChange={() => {
                    setChannelIdActive(id)
                  }}
                />
                <span>{text}</span>
              </label>
              <label className={clsx('dtc-widget-frequency-selector__priceincentiveText', classes.root)} htmlFor={`subscription-${sku}-${id}`} dangerouslySetInnerHTML={{ __html: localizedpriceIncentiveText }}></label>
            </Grid>
          </React.Fragment>
        )))
      }
    </Grid>
  )
}

export default Sentry.withProfiler(WidgetFrequencySelector, { name: 'WidgetFrequencySelector' })
